import config from '../config'
import {
  getNextCode,
  saveOrUpdateClient,
  saveOrUpdateInvoice,
  updateBusinessDetails,
} from '../api'
import fetcher from '../api/fetcher.mjs'
import {
  applyServerCodeToSaveBody,
  GuestHydrationExecutionPlan,
  isInvoiceCodeConflictError,
  withResolvedClientId,
  withResolvedInvoiceId,
} from './guestHydrationOrchestrator'

export type ExecuteGuestHydrationResult =
  | { ok: true; invoiceId: string; postSaveAction: GuestHydrationExecutionPlan['postSaveAction'] }
  | { ok: false; error: unknown }

const uploadBusinessLogo = async (logo: string): Promise<void> => {
  await fetcher(`${config.REACT_APP_API_URL}/api/user/business/logo`, {
    method: 'POST',
    body: JSON.stringify({ logo }),
  })
}

const resolveClientId = async (plan: GuestHydrationExecutionPlan): Promise<string> => {
  if (plan.clientResolution.action === 'link') {
    return plan.clientResolution.clientId
  }

  const response = await saveOrUpdateClient('/api/clients', {
    arg: { body: plan.clientResolution.payload },
  })
  const insertedId = response?.data?.insertedId ?? response?.data?._id
  if (!insertedId) {
    throw new Error('Could not create client from guest draft')
  }
  return String(insertedId)
}

const saveInvoiceWithCodeFallback = async (
  body: Record<string, unknown>,
  guestCode: string,
): Promise<string> => {
  try {
    const response = await saveOrUpdateInvoice('/api/invoices', { arg: { body } })
    const insertedId = response?.data?.insertedId
    if (!insertedId) {
      throw new Error('Could not save guest invoice')
    }
    return String(insertedId)
  } catch (error) {
    if (!guestCode || !isInvoiceCodeConflictError(error)) {
      throw error
    }
    const nextCodeResponse = await getNextCode()
    const serverCode = nextCodeResponse?.data
    if (!serverCode) {
      throw error
    }
    const retryBody = applyServerCodeToSaveBody(body, serverCode)
    const response = await saveOrUpdateInvoice('/api/invoices', { arg: { body: retryBody } })
    const insertedId = response?.data?.insertedId
    if (!insertedId) {
      throw new Error('Could not save guest invoice after code conflict')
    }
    return String(insertedId)
  }
}

export const executeGuestHydrationPlan = async (
  plan: GuestHydrationExecutionPlan,
): Promise<ExecuteGuestHydrationResult> => {
  try {
    if (plan.profileUpdate.shouldUpdate) {
      if (plan.profileUpdate.businessDetailsPatch) {
        const profileResult = await updateBusinessDetails('/api/user/business', {
          arg: { body: plan.profileUpdate.businessDetailsPatch },
        })
        if (!profileResult) {
          throw new Error('Could not update profile from guest draft')
        }
      }
      if (plan.profileUpdate.logoUpload) {
        await uploadBusinessLogo(plan.profileUpdate.logoUpload)
      }
    }

    const clientId = await resolveClientId(plan)
    const invoiceId = await saveInvoiceWithCodeFallback(
      withResolvedClientId(plan.invoiceSaveBody, clientId),
      plan.guestCode,
    )

    return {
      ok: true,
      invoiceId,
      postSaveAction: withResolvedInvoiceId(plan, invoiceId).postSaveAction,
    }
  } catch (error) {
    return { ok: false, error }
  }
}
